import { useEffect } from 'react'
import type { ReactNode } from 'react'
import { ArrowUpRight, Sparkles } from 'lucide-react'
import PageHeader from '@/components/layout/PageHeader'
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from '@/components/ui'
import { useSettingsStore } from '@/stores/useSettingsStore'

const STEPS = [
  {
    title: 'Connect',
    body: 'Add a WP Engine install name and one of your SSH keys. Argus runs WP-CLI over the connection to confirm it works before saving anything.'
  },
  {
    title: 'Fetch',
    body: 'Argus reads the whole media library through that connection and stores every image, including the alt text WordPress already has.'
  },
  {
    title: 'Generate',
    body: 'For each image you pick, the Claude Code CLI on the server host looks at the file and writes a short, screen-reader-friendly description.'
  },
  {
    title: 'Review and save',
    body: 'Edit anything that misses the mark, approve what you want, and Argus writes it back with WP-CLI. Nothing reaches your site until you approve it.'
  }
]

interface FactProps {
  label: string
  children: ReactNode
}

function Fact({ label, children }: FactProps): React.ReactElement {
  return (
    <div>
      <p className="text-[11px] font-semibold tracking-[0.07em] text-fg-subtle uppercase">{label}</p>
      <p className="mt-1 text-sm text-fg">{children}</p>
    </div>
  )
}

export default function AboutPage(): React.ReactElement {
  const status = useSettingsStore((state) => state.status)
  const loadStatus = useSettingsStore((state) => state.loadStatus)

  useEffect(() => {
    void loadStatus()
  }, [loadStatus])

  return (
    <div className="mx-auto w-full max-w-3xl">
      <PageHeader
        title="About Argus"
        description="Accessible alt text for WordPress media libraries, at the scale a real site actually has."
      />

      <div className="space-y-6">
        <Card>
          <CardBody className="space-y-5">
            <div className="flex items-start gap-4">
              <span className="grid size-11 shrink-0 place-items-center rounded-card bg-accent text-accent-fg shadow-sheet">
                <Sparkles className="size-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold tracking-tight text-fg">Argus</h2>
                <p className="text-sm text-fg-muted">
                  {status?.version ? `Version ${status.version}` : 'Self-hosted web app'}
                </p>
              </div>
            </div>

            <p className="text-sm leading-relaxed text-fg-muted">
              Missing alt text is the most common accessibility problem on a content-heavy
              WordPress site, and the hardest to fix by hand — nobody is going to write
              descriptions for four thousand attachments one edit screen at a time. Argus connects
              to your WP Engine installs over SSH, pulls the media library in, generates alt text
              with the Claude Code CLI, and writes the approved text back to WordPress.
            </p>

            <p className="text-sm leading-relaxed text-fg-muted">
              It is multi-user by design. Your sites, SSH keys and job history belong to your
              account and are never visible to anyone else, admins included. What everybody does
              share is the host: one Claude Code CLI and one job queue, which is why generation
              waits its turn when the instance is busy.
            </p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>How it works</CardTitle>
            <CardDescription>Four steps, and you approve everything before it ships.</CardDescription>
          </CardHeader>
          <CardBody>
            <ol className="space-y-4">
              {STEPS.map((step, index) => (
                <li key={step.title} className="flex gap-3.5">
                  <span
                    aria-hidden="true"
                    className="grid size-6 shrink-0 place-items-center rounded-full bg-accent-soft text-xs font-semibold text-accent"
                  >
                    {index + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-fg">{step.title}</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-fg-muted">{step.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Under the hood</CardTitle>
            <CardDescription>
              No third-party service sits between Argus and your sites — it is your server, your
              SSH keys and your Claude Code CLI.
            </CardDescription>
          </CardHeader>
          <CardBody>
            <div className="grid gap-5 sm:grid-cols-2">
              <Fact label="Interface">React 19, Vite and Tailwind CSS</Fact>
              <Fact label="API">Fastify with server-sent events for live job progress</Fact>
              <Fact label="Data">PostgreSQL via Prisma</Fact>
              <Fact label="Alt text">Claude Code CLI, running on the server host</Fact>
              <Fact label="Connectivity">SSH and WP-CLI to WP Engine</Fact>
              <Fact label="Version">{status?.version ?? 'Not reported'}</Fact>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="space-y-4">
            <p className="text-[11px] font-semibold tracking-[0.07em] text-fg-subtle uppercase">
              Developer
            </p>

            <div className="flex items-center gap-4">
              <span
                aria-hidden="true"
                className="grid size-12 shrink-0 place-items-center rounded-full bg-accent-soft text-sm font-semibold text-accent"
              >
                WS
              </span>
              <div className="min-w-0">
                <p className="text-base font-semibold text-fg">Wasim Sayyed</p>
                <p className="text-xs text-fg-muted">Director of Engineering Operations</p>
                <a
                  href="https://9cloudwebworks.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1.5 inline-flex items-center gap-1 text-sm font-medium text-accent underline decoration-transparent underline-offset-2 transition-colors hover:decoration-current"
                >
                  9 Cloud Web Works
                  <ArrowUpRight className="size-3.5" aria-hidden="true" />
                </a>
              </div>
            </div>

            <p className="text-xs leading-relaxed text-fg-subtle">
              9 Cloud Web Works specializes in WordPress development, web performance, and
              AI-powered tools for content management. Built on the philosophy that great web
              experiences start with accessible, well-structured content.
            </p>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export { AboutPage }
